import { useState } from 'react';
import { ArrowUpRight, Globe, Code, Smartphone, Monitor } from 'lucide-react';

const ProjectCard = ({ 
  title, 
  subtitle, 
  platform, 
  industry, 
  tags, 
  gradientFrom, 
  gradientTo,
  accentColor 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="relative group cursor-pointer transform transition-all duration-500 hover:scale-105 hover:-translate-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Card */}
      <div 
        className="relative overflow-hidden rounded-2xl shadow-lg transition-all duration-500 group-hover:shadow-2xl"
        style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
      >
        {/* Image Container */}
        <div className="relative h-64 md:h-72 overflow-hidden">
          {/* Placeholder for image - you'll replace this */}
          <div className="w-full h-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center">
              <Monitor className="w-10 h-10 text-white" />
            </div>
          </div>
          
          {/* Hover Overlay */}
          <div 
            className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-all duration-500 flex items-center justify-center ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <button 
              className="group/btn bg-white text-gray-800 px-6 py-3 rounded-full font-semibold flex items-center gap-2 transform transition-all duration-300 hover:scale-110 hover:bg-gray-100"
            >
              <Globe className="w-5 h-5" />
              Live Demo
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
            </button>
          </div>

          {/* Floating Elements */}
          <div className="absolute top-4 right-4">
            <div 
              className="w-12 h-12 rounded-lg backdrop-blur-md flex items-center justify-center transition-all duration-500"
              style={{ background: 'rgba(255, 255, 255, 0.2)' }}
            >
              <Code className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 bg-white">
          <h3 className="text-xl font-bold mb-2" style={{ color: '#2D3748' }}>
            {title}
          </h3>
          <p className="text-sm mb-4" style={{ color: '#718096' }}>
            {subtitle}
          </p>

          {/* Platform Info */}
          <div className="flex items-center gap-2 mb-3">
            <Monitor className="w-4 h-4" style={{ color: '#4A5568' }} />
            <span className="text-sm font-medium" style={{ color: '#4A5568' }}>
              Platform:
            </span>
            <span className="text-sm" style={{ color: '#718096' }}>
              {platform}
            </span>
          </div>

          {/* Industry Info */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: accentColor }}></div>
            <span className="text-sm font-medium" style={{ color: '#4A5568' }}>
              Industry:
            </span>
            <span className="text-sm" style={{ color: '#718096' }}>
              {industry}
            </span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 rounded-full text-xs font-medium text-white transition-transform duration-300 hover:scale-105"
                style={{ backgroundColor: accentColor }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Animated Border */}
        <div 
          className={`absolute inset-0 rounded-2xl transition-all duration-500 pointer-events-none ${
            isHovered ? 'ring-4 ring-white/30' : ''
          }`}
        ></div>
      </div>

      {/* Floating Shadow */}
      <div 
        className={`absolute inset-0 rounded-2xl transition-all duration-500 -z-10 ${
          isHovered ? 'blur-xl opacity-30' : 'blur-0 opacity-0'
        }`}
        style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
      ></div>
    </div>
  );
};

const PortfolioShowcase = () => {
  const projects = [
    {
      title: "E-Commerce Platform",
      subtitle: "Full-stack online shopping solution with payment integration",
      platform: "Web & Mobile",
      industry: "Retail & E-commerce",
      tags: ["UI & UX", "Frontend", "Backend"],
      gradientFrom: "#FF6B6B",
      gradientTo: "#FF8E53",
      accentColor: "#E53E3E"
    },
    {
      title: "Healthcare Management System",
      subtitle: "Comprehensive patient management and appointment scheduling",
      platform: "Web & Mobile",
      industry: "Healthcare & Medical",
      tags: ["UI & UX", "Frontend", "Backend"],
      gradientFrom: "#4ECDC4",
      gradientTo: "#44A08D",
      accentColor: "#38B2AC"
    },
    {
      title: "Real Estate Portal",
      subtitle: "Property listing and management platform",
      platform: "Web",
      industry: "Real Estate",
      tags: ["UI & UX", "Frontend", "Backend"],
      gradientFrom: "#667eea",
      gradientTo: "#764ba2",
      accentColor: "#5A67D8"
    },
    {
      title: "Learning Management System",
      subtitle: "Online education platform with course management",
      platform: "Web & Mobile",
      industry: "Education & Training",
      tags: ["UI & UX", "Frontend", "Backend"],
      gradientFrom: "#f093fb",
      gradientTo: "#f5576c",
      accentColor: "#ED64A6"
    },
    {
      title: "Food Delivery App",
      subtitle: "Restaurant ordering and delivery tracking system",
      platform: "Mobile & Web",
      industry: "Food & Beverage",
      tags: ["UI & UX", "Mobile App", "Backend"],
      gradientFrom: "#ffecd2",
      gradientTo: "#fcb69f",
      accentColor: "#F6AD55"
    },
    {
      title: "Financial Dashboard",
      subtitle: "Investment tracking and portfolio management tool",
      platform: "Web",
      industry: "Finance & Banking",
      tags: ["UI & UX", "Frontend", "Analytics"],
      gradientFrom: "#a8edea",
      gradientTo: "#fed6e3",
      accentColor: "#4FD1C7"
    }
  ];

  return (
    <div className="min-h-screen py-16 px-4" style={{ background: '#F7FAFC' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: '#2D3748' }}
          >
            Our Portfolio
          </h1>
          <p 
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: '#718096' }}
          >
            Showcasing our expertise in creating innovative digital solutions across various industries
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={index} {...project} />
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <button 
            className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center gap-3 mx-auto"
          >
            View All Projects
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioShowcase;